/**
 * Utility functions for date/time operations
 *
 * These utilities help handle date conversions between:
 * - UTC (stored in database)
 * - User's preferred timezone (for display)
 * - Browser's local timezone (for form inputs)
 */

/**
 * Default timezone used when no user preference is available
 */
export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Returns today's date in local timezone formatted as YYYY-MM-DD
 * Use this for date input max/min attributes to ensure correct local date
 */
export function getLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns a datetime in local timezone formatted as YYYY-MM-DDTHH:mm
 * Use this for datetime-local input values to ensure correct local datetime
 */
export function getLocalDateTimeString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats a date string (ISO or other) to local date format YYYY-MM-DD for date inputs
 */
export function formatDateForInput(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return getLocalDateString(date);
}

/**
 * Extracts the calendar date (YYYY-MM-DD) from an ISO datetime using UTC.
 * Use for date-only fields (e.g. workedDate) stored as UTC midnight so the
 * same date shows when adding, in the list, and when editing.
 */
export function getDateOnlyFromISO(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Adds calendar days to a YYYY-MM-DD string (UTC calendar arithmetic).
 */
export function addDaysToDateString(dateString: string, days: number): string {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return '';

  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Formats a date string (ISO or other) to local datetime format YYYY-MM-DDTHH:mm for datetime-local inputs
 * Converts UTC datetime from database to user's local timezone for display
 */
export function formatDateTimeForInput(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return getLocalDateTimeString(date);
}

/**
 * Gets the UTC offset in minutes for a specific timezone at a specific date.
 * This accounts for DST changes throughout the year.
 *
 * @param date - The date to check the offset for
 * @param timezone - IANA timezone identifier
 * @returns Offset in minutes (positive = ahead of UTC, negative = behind UTC)
 */
function getTimezoneOffset(date: Date, timezone: string): number {
  // Format the date in both UTC and the target timezone
  const utcFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const utcParts = utcFormatter.formatToParts(date);
  const tzParts = tzFormatter.formatToParts(date);

  const getPartValue = (parts: Intl.DateTimeFormatPart[], type: string): number => {
    const value = parts.find(p => p.type === type)?.value || '0';
    return parseInt(value, 10);
  };

  const utcYear = getPartValue(utcParts, 'year');
  const utcMonth = getPartValue(utcParts, 'month');
  const utcDay = getPartValue(utcParts, 'day');
  const utcHour = getPartValue(utcParts, 'hour');
  const utcMinute = getPartValue(utcParts, 'minute');

  const tzYear = getPartValue(tzParts, 'year');
  const tzMonth = getPartValue(tzParts, 'month');
  const tzDay = getPartValue(tzParts, 'day');
  const tzHour = getPartValue(tzParts, 'hour');
  const tzMinute = getPartValue(tzParts, 'minute');

  // Calculate the difference in minutes
  const utcDate = new Date(Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, utcMinute));
  const tzDate = new Date(Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute));

  return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
}

/**
 * Converts a datetime-local string (YYYY-MM-DDTHH:mm) to UTC ISO 8601 string.
 *
 * IMPORTANT: The datetime-local value is interpreted in the specified timezone,
 * NOT the browser's local timezone. This ensures consistent behavior regardless
 * of where the user's browser is running.
 *
 * Example: User in IST (UTC+5:30) selects "2025-12-03T14:30"
 * - Input: "2025-12-03T14:30", timezone: "Asia/Kolkata"
 * - Output: "2025-12-03T09:00:00.000Z" (UTC equivalent)
 *
 * @param datetimeLocal - Date string in YYYY-MM-DDTHH:mm format from datetime-local input
 * @param timezone - IANA timezone identifier (defaults to Asia/Kolkata)
 * @returns ISO 8601 string in UTC, or empty string if input is empty
 */
export function datetimeLocalToUTC(
  datetimeLocal: string | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  if (!datetimeLocal) return '';

  // Parse datetime-local format: "YYYY-MM-DDTHH:mm"
  const [datePart, timePart] = datetimeLocal.split('T');
  if (!datePart || !timePart) {
    console.error('Invalid datetime-local format:', datetimeLocal);
    return '';
  }

  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
    console.error('Invalid datetime-local format:', datetimeLocal);
    return '';
  }

  // Create a reference date to get the timezone offset
  // Use a date close to the target to account for DST
  const referenceDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const offsetMinutes = getTimezoneOffset(referenceDate, timezone);

  // The user entered time is in the specified timezone
  // We need to subtract the offset to get UTC
  // e.g., 14:30 IST (UTC+5:30) → 14:30 - 5:30 = 09:00 UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
  utcDate.setMinutes(utcDate.getMinutes() - offsetMinutes);

  const result = utcDate.toISOString();

  // Debug logging
  console.log('[datetimeLocalToUTC] Input:', datetimeLocal, 'Timezone:', timezone, 'Offset (min):', offsetMinutes, 'Result:', result);

  return result;
}

/**
 * Converts a local date string (YYYY-MM-DD) to an ISO 8601 string at UTC noon.
 *
 * This prevents timezone-related date shifting issues. By storing dates at 12:00:00 UTC,
 * the date remains the same in all timezones from UTC-12 to UTC+14.
 *
 * Example: User in IST selects "2025-12-03"
 * - Without this fix: "2025-12-03" → midnight IST → "2025-12-02T18:30:00Z" (wrong date!)
 * - With this fix: "2025-12-03" → "2025-12-03T12:00:00.000Z" (correct date)
 *
 * @param dateString - Date string in YYYY-MM-DD format from date input
 * @returns ISO 8601 string at UTC noon, or empty string if input is empty
 */
export function dateToUTCNoon(dateString: string | null | undefined): string {
  if (!dateString) return '';

  // Parse the date parts directly to avoid timezone interpretation
  const [year, month, day] = dateString.split('-').map(Number);

  // Create ISO string at UTC noon (12:00:00.000Z)
  // This ensures the date is the same in all timezones from UTC-12 to UTC+14
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00.000Z`;
}

/**
 * Extracts the date part from an ISO 8601 string for display in date inputs.
 * Works correctly regardless of what time the date was stored at.
 *
 * This function converts from stored UTC time back to the user's local date,
 * ensuring consistent round-trip behavior with dateToUTCNoon().
 *
 * @param isoString - ISO 8601 datetime string from backend
 * @returns Date string in YYYY-MM-DD format for date input, or empty string if input is empty
 */
export function formatDateFromUTC(isoString: string | null | undefined): string {
  if (!isoString) return '';

  // Parse the ISO string and get the local date representation
  const date = new Date(isoString);
  return getLocalDateString(date);
}

export interface SyncRecencyStatus {
  status: 'recent' | 'earlier' | 'never';
  label: string;
  color: string;
  hoursAgo?: number;
  daysAgo?: number;
}

/**
 * Determines if a resource sync is recent or earlier
 * Recent = within last 24 hours
 * Earlier = more than 24 hours ago
 * Never = never synced
 */
export function getSyncRecencyStatus(syncedAt: string | null): SyncRecencyStatus {
  if (!syncedAt) {
    return {
      status: 'never',
      label: 'Never Synced',
      color: 'gray',
    };
  }

  const now = new Date();
  const syncDate = new Date(syncedAt);
  const diffMs = now.getTime() - syncDate.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 24) {
    return {
      status: 'recent',
      label: diffHours === 0 ? 'Just Now' : `${diffHours}h ago`,
      color: 'green',
      hoursAgo: diffHours,
    };
  }

  if (diffDays === 1) {
    return {
      status: 'earlier',
      label: '1 day ago',
      color: 'yellow',
      daysAgo: 1,
    };
  }

  if (diffDays < 7) {
    return {
      status: 'earlier',
      label: `${diffDays} days ago`,
      color: 'yellow',
      daysAgo: diffDays,
    };
  }

  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return {
      status: 'earlier',
      label: weeks === 1 ? '1 week ago' : `${weeks} weeks ago`,
      color: 'orange',
      daysAgo: diffDays,
    };
  }

  const months = Math.floor(diffDays / 30);
  return {
    status: 'earlier',
    label: months === 1 ? '1 month ago' : `${months} months ago`,
    color: 'red',
    daysAgo: diffDays,
  };
}

/**
 * Converts a UTC datetime to a specific timezone for display in datetime-local inputs.
 * This is useful for pre-filling form fields with dates from the database.
 *
 * @param dateInput - UTC date from database (ISO string or Date object)
 * @param timezone - Target timezone (IANA timezone identifier)
 * @returns String in YYYY-MM-DDTHH:mm format for datetime-local input
 */
export function formatDateTimeForInputTz(
  dateInput: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  if (!dateInput) return '';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '';

  try {
    // Format in the target timezone using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';
    const hour = parts.find(p => p.type === 'hour')?.value || '';
    const minute = parts.find(p => p.type === 'minute')?.value || '';

    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    // Fallback to browser local timezone
    return formatDateTimeForInput(typeof dateInput === 'string' ? dateInput : dateInput.toISOString());
  }
}

/**
 * Converts a UTC date to a specific timezone for display in date inputs.
 *
 * @param dateInput - UTC date from database (ISO string or Date object)
 * @param timezone - Target timezone (IANA timezone identifier)
 * @returns String in YYYY-MM-DD format for date input
 */
export function formatDateForInputTz(
  dateInput: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  if (!dateInput) return '';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '';

  try {
    // Format in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = formatter.formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    // Fallback to browser local timezone
    return formatDateForInput(typeof dateInput === 'string' ? dateInput : dateInput.toISOString());
  }
}

/**
 * Gets the current date string in a specific timezone.
 *
 * @param timezone - Target timezone (IANA timezone identifier)
 * @returns String in YYYY-MM-DD format
 */
export function getDateStringInTimezone(timezone: string = DEFAULT_TIMEZONE): string {
  return formatDateForInputTz(new Date(), timezone);
}

/**
 * Returns every calendar date (YYYY-MM-DD) from startDate through endDate inclusive.
 */
export function expandDateRange(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate) return [];

  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  if (
    [startYear, startMonth, startDay, endYear, endMonth, endDay].some((value) => Number.isNaN(value))
  ) {
    return [];
  }

  const dates: string[] = [];
  const current = new Date(Date.UTC(startYear, startMonth - 1, startDay));
  const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));

  while (current <= end) {
    const y = current.getUTCFullYear();
    const m = String(current.getUTCMonth() + 1).padStart(2, '0');
    const d = String(current.getUTCDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
}

/** Returns true if the YYYY-MM-DD date falls on Saturday or Sunday (UTC calendar). */
export function isWeekendDateString(dateString: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false;
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = date.getUTCDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

/** Filters copy target dates, optionally excluding weekends. */
export function filterCopyTargetDates(dates: string[], includeWeekends: boolean): string[] {
  if (includeWeekends) return dates;
  return dates.filter((date) => !isWeekendDateString(date));
}

/** Resolves eligible dates for multi-day create/copy from a range. */
export function resolveMultiDayDates(options: {
  rangeStartDate: string;
  rangeEndDate: string;
  maxDate: string;
  includeWeekends: boolean;
  excludeDate?: string;
}): string[] {
  const { rangeStartDate, rangeEndDate, maxDate, includeWeekends, excludeDate } = options;
  if (!rangeStartDate || !rangeEndDate || rangeStartDate > rangeEndDate) return [];

  const allDates = expandDateRange(rangeStartDate, rangeEndDate);
  const upToMax = maxDate ? allDates.filter((date) => date <= maxDate) : allDates;
  const withoutExcluded = excludeDate ? upToMax.filter((date) => date !== excludeDate) : upToMax;
  return filterCopyTargetDates(withoutExcluded, includeWeekends);
}

/**
 * Gets the current datetime string in a specific timezone.
 *
 * @param timezone - Target timezone (IANA timezone identifier)
 * @returns String in YYYY-MM-DDTHH:mm format
 */
export function getDateTimeStringInTimezone(timezone: string = DEFAULT_TIMEZONE): string {
  return formatDateTimeForInputTz(new Date(), timezone);
}
